"use client";

import React, { useEffect, useState, useRef } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { Database, Play, Loader2, ArrowLeft, Search, Upload, PieChart, MessageSquare, Table, Plus, X, FileCheck2, Save } from "lucide-react";
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import {
  getProcedureFileUsageForWorkspace,
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry
} from "@/utils/fileUsageReference";
import { formatNumber } from "@/utils/formatting";

interface PPECwipAnalysisProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}
interface ExcelColumn { name: string; value: string; }

export default function PPECwipAnalysis({ onBack, setActiveSection }: PPECwipAnalysisProps) {
  const [cwipFile, setCwipFile] = useState<string>("");
  const [cutoffDate, setCutoffDate] = useState<string>("");
  const [cutoffDateISO, setCutoffDateISO] = useState<string>("");
  const [grnDate, setGrnDate] = useState<string>("");
  const [grnDateISO, setGrnDateISO] = useState<string>("");
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [amountColumn, setAmountColumn] = useState<string>("");
  const [dateColumn, setDateColumn] = useState<string>("");
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileUrl, setFileUrl] = useState<string>("");

  // File search states
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");

  // Composition Analysis states
  const [cwipCompositionColumns, setCwipCompositionColumns] = useState<Set<string>>(new Set());
  const [showCwipCompositionDropdown, setShowCwipCompositionDropdown] = useState(false);
  const [cwipCompositionColumnsSearch, setCwipCompositionColumnsSearch] = useState("");
  const [cwipCompositionAmountCol, setCwipCompositionAmountCol] = useState<string>("");
  const [isRunningCwipCompositionAnalysis, setIsRunningCwipCompositionAnalysis] = useState(false);
  const cwipCompositionDropdownRef = useRef<HTMLDivElement>(null);
  const [loadedCwipCompositionData, setLoadedCwipCompositionData] = useState<Record<string, any>>({});
  const [isLoadingCwipCompositionData, setIsLoadingCwipCompositionData] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Completeness Check state
  interface UserReconciliation {
    reason: string;
    amount: number;
  }
  const [cwipRegisterAmount, setCwipRegisterAmount] = useState<number>(0);
  const [isLoadingCwipRegisterAmount, setIsLoadingCwipRegisterAmount] = useState(false);
  const [subleadAmount, setSubleadAmount] = useState<number>(0);
  const [isLoadingSubleadAmount, setIsLoadingSubleadAmount] = useState(false);
  const [userReconciliations, setUserReconciliations] = useState<UserReconciliation[]>([]);
  const [isSavingReconciliation, setIsSavingReconciliation] = useState(false);
  const [isLoadingReconciliation, setIsLoadingReconciliation] = useState(false);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `PPE_CWIP_ANALYSIS_${normalizedSection}`;
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Property, Plant & Equipment", "CWIP Analysis");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_PPE_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_PPE_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "CWIP-ANALYSIS-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Auto-trigger search on mount
  useEffect(() => {
    searchForFiles();
    loadReconciliationData();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_PPE_CWIP");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(',')[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_PPE_CWIP"],
              });

              if (result.success) {
                setCwipFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!cwipFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(cwipFile);
        if (result.ok && result.columns) {
          setExcelColumns(result.columns.map((c: string) => ({ name: c, value: c })));
          // Save file name to file usage reference
          const fileName = cwipFile.includes(' (') ? cwipFile.split(' (')[0] : cwipFile;
          await updateProcedureFileUsageForWorkspace("Property, Plant & Equipment", "CWIP Analysis", [{
            "Search for files": fileName
          }]);
          await loadFileUsageReference();
        } else {
          console.error("Failed to load Excel columns:", result.error);
        }
      }
    } catch (e) {
      console.error("Error loading Excel columns:", e);
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  // Convert ISO date (YYYY-MM-DD) to mm/dd/yyyy format
  const formatDateForBackend = (isoDate: string): string => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Convert mm/dd/yyyy to ISO format (YYYY-MM-DD)
  const parseDateFromBackend = (dateStr: string): string => {
    if (!dateStr) return "";
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return "";
  };

  const handleCutoffDateChange = (isoDate: string) => {
    setCutoffDateISO(isoDate);
    setCutoffDate(formatDateForBackend(isoDate));
  };

  const handleGrnDateChange = (isoDate: string) => {
    setGrnDateISO(isoDate);
    setGrnDate(formatDateForBackend(isoDate));
  };

  const handleBack = () => {
    if (setActiveSection) {
      setActiveSection("ppe-substantive");
    } else if (onBack) {
      onBack();
    }
  };

  // Close composition dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cwipCompositionDropdownRef.current && !cwipCompositionDropdownRef.current.contains(event.target as Node)) {
        setShowCwipCompositionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCwipCompositionColumnToggle = (column: string) => {
    setCwipCompositionColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(column)) {
        newSet.delete(column);
      } else {
        newSet.add(column);
      }
      return newSet;
    });
  };

  const runCwipCompositionAnalysis = async () => {
    if (cwipCompositionColumns.size === 0 || !cwipCompositionAmountCol || !cwipFile) return;

    setIsRunningCwipCompositionAnalysis(true);
    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_ppe_cwip_composition_analysis", {
          inputFiles: [],
          options: {
            group_columns: Array.from(cwipCompositionColumns),
            amount_column: cwipCompositionAmountCol,
          },
        });

        if (result.ok && result.runId) {
          const unsubscribe = window.payroll.onProgress((payload: any) => {
            if (payload.runId !== result.runId) return;

            if (payload.status === "success") {
              try {
                const lines = String(payload.stdout || '').split('\n');
                const jsonLine = lines.find((l: string) => l.trim().startsWith('{') && l.includes('success'));
                if (jsonLine) {
                  const parsed = JSON.parse(jsonLine);
                  if (parsed.success) {
                    console.log("CWIP Composition Analysis completed:", parsed);
                  }
                }
              } catch (e) {
                console.error("Error parsing result:", e);
              }
              setIsRunningCwipCompositionAnalysis(false);
              unsubscribe();
            } else if (payload.status === "error") {
              console.error("CWIP composition analysis error:", payload.error);
              setIsRunningCwipCompositionAnalysis(false);
              unsubscribe();
            }
          });
        } else {
          setIsRunningCwipCompositionAnalysis(false);
        }
      } else {
        setIsRunningCwipCompositionAnalysis(false);
      }
    } catch (error) {
      console.error("Error running CWIP composition analysis:", error);
      setIsRunningCwipCompositionAnalysis(false);
    }
  };

  // Helper function to extract JSON from stdout (handles both composition analysis and completeness check)
  const extractJSONFromStdout = (stdout: string): any | null => {
    if (!stdout) return null;
    try {
      const normalized = stdout.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const lines = normalized.split('\n');

      for (const line of lines) {
        const trimmed = line.trim();
        // Check for composition analysis format (has "success" and "data")
        if (trimmed.startsWith('{') && trimmed.includes('"success"') && trimmed.includes('"data"')) {
          try {
            const cleanLine = trimmed.replace(/\r$/, '').trim();
            const parsed = JSON.parse(cleanLine);
            if (parsed.success && parsed.data) {
              return parsed.data;
            }
          } catch (parseError) {
            // Try regex extraction
            try {
              const jsonMatch = trimmed.match(/\{.*"success".*"data".*\}/);
              if (jsonMatch) {
                const parsed = JSON.parse(jsonMatch[0]);
                if (parsed.success && parsed.data) {
                  return parsed.data;
                }
              }
            } catch (regexError) {
              // Continue to next line
            }
          }
        }
        // Check for completeness check format (has "sum")
        if (trimmed.startsWith('{') && trimmed.includes('"sum"')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (parsed.sum !== undefined && (parsed.success || parsed.ok)) {
              return parsed;
            }
          } catch (e) {
            // Continue to next line
          }
        }
      }
      return null;
    } catch (error) {
      console.error("Error extracting JSON from stdout:", error);
      return null;
    }
  };

  const loadCwipCompositionCharts = async () => {
    setIsLoadingCwipCompositionData(true);
    try {
      if (!window.payroll?.run) {
        setIsLoadingCwipCompositionData(false);
        return;
      }

      try {
        // Set up listener FIRST, before calling run(), to ensure we catch all payloads
        let allStdout = "";
        let statusReceived = false;
        let payloadCount = 0;
        let chartData: Record<string, any> | null = null;
        let expectedRunId: string | null = null;

        const pendingPayloads: any[] = []; // Store payloads that arrive before we know the runId
        const allPayloads: any[] = [];

        const unsubscribe = window.payroll.onProgress((payload: any) => {
          allPayloads.push(payload);
          payloadCount++;
          const payloadRunId = String(payload.runId || '');

          // If we don't have expectedRunId yet, store the payload
          if (!expectedRunId) {
            pendingPayloads.push(payload);
            return;
          }

          const matches = payloadRunId === expectedRunId;

          if (!matches) {
            return;
          }

          // Accumulate stdout from any payload that has it
          if (payload.stdout) {
            allStdout = String(payload.stdout);
            try {
              const extracted = extractJSONFromStdout(allStdout);
              if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                chartData = extracted;
              }
            } catch (e) {
              console.error("Error extracting JSON:", e);
            }
          }

          // Check for success status
          if (payload.status === "success") {
            statusReceived = true;
            if (payload.stdout) {
              allStdout = String(payload.stdout);
            }
            // Try to extract data one more time with the final stdout
            if (!chartData && allStdout) {
              try {
                const extracted = extractJSONFromStdout(allStdout);
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                }
              } catch (e) {
                console.error("Error extracting JSON on success:", e);
              }
            }
          }

          // Check for error status
          if (payload.status === "error" || payload.error) {
            statusReceived = true;
          }
        });

        const chartResult = await window.payroll.run("download_ppe_cwip_composition_analysis", {
          inputFiles: [],
          options: {},
        });

        if (chartResult.ok && chartResult.runId) {
          expectedRunId = String(chartResult.runId);

          // Process any pending payloads that arrived before we knew the runId
          for (const pendingPayload of pendingPayloads) {
            const pendingRunId = String(pendingPayload.runId || '');
            if (pendingRunId === expectedRunId) {
              const payload = pendingPayload;
              payloadCount++;

              if (payload.stdout) {
                allStdout = String(payload.stdout);
                try {
                  const extracted = extractJSONFromStdout(allStdout);
                  if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                    chartData = extracted;
                  }
                } catch (e) {
                  console.error("Error extracting JSON from pending payload:", e);
                }
              }

              if (payload.status === "success") {
                statusReceived = true;
              }
            }
          }

          // Wait for completion
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (statusReceived) {
                clearInterval(checkInterval);
                unsubscribe();
                resolve();
              }
            }, 100);
            setTimeout(() => {
              clearInterval(checkInterval);
              unsubscribe();
              if (!statusReceived) {
                // If we have data but no status, that's okay - use the data
                if (chartData && Object.keys(chartData).length > 0) {
                  statusReceived = true;
                }
              }
              resolve();
            }, 30000); // 30 second timeout
          });

          // Store chart data
          // Fallback: if strict runId matching didn't produce data, scan all payloads.
          if ((!chartData || Object.keys(chartData).length === 0) && allPayloads.length > 0) {
            for (const payload of allPayloads) {
              if (!payload?.stdout) continue;
              try {
                const extracted = extractJSONFromStdout(String(payload.stdout));
                if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
                  chartData = extracted;
                  break;
                }
              } catch {
                // Ignore malformed payload and continue.
              }
            }
          }

          // Store chart data
          if (chartData && Object.keys(chartData).length > 0) {
            const clonedChartData = JSON.parse(JSON.stringify(chartData));
            setLoadedCwipCompositionData(clonedChartData);
          } else {
            setLoadedCwipCompositionData({});
          }
        } else {
          unsubscribe();
        }
      } catch (error) {
        console.error("Error loading chart data:", error);
      }
    } catch (error) {
      console.error("Error in loadCwipCompositionCharts:", error);
      setLoadedCwipCompositionData({});
    } finally {
      setIsLoadingCwipCompositionData(false);
    }
  };

  // Auto-trigger load charts on mount
  useEffect(() => {
    loadCwipCompositionCharts();
  }, []);

  // Load CWIP Register amount (sum of amount column from uploaded file)
  const loadCwipRegisterAmount = async () => {
    if (!cwipFile || !amountColumn) {
      alert('Please select a file and map the Amount column first');
      return;
    }
    setIsLoadingCwipRegisterAmount(true);
    try {
      if (!window.payroll?.run) {
        alert('Payroll API not available');
        setIsLoadingCwipRegisterAmount(false);
        return;
      }

      let allStdout = "";
      let expectedRunId: string | null = null;
      let sumValue: number | null = null;

      const unsubscribe = window.payroll.onProgress((payload: any) => {
        const payloadRunId = String(payload.runId || '');
        if (!expectedRunId && payloadRunId) {
          expectedRunId = payloadRunId;
        }
        if (expectedRunId && payloadRunId !== expectedRunId) {
          return;
        }

        if (payload.stdout) {
          allStdout = String(payload.stdout);
          const extracted = extractJSONFromStdout(allStdout);
          if (extracted && extracted.sum !== undefined) {
            sumValue = Number(extracted.sum);
            setCwipRegisterAmount(sumValue);
            setIsLoadingCwipRegisterAmount(false);
            unsubscribe();
            return;
          }
        }

        if (payload.status === 'success') {
          if (allStdout && !sumValue) {
            const extracted = extractJSONFromStdout(allStdout);
            if (extracted && extracted.sum !== undefined) {
              sumValue = Number(extracted.sum);
              setCwipRegisterAmount(sumValue);
            }
          }
          setIsLoadingCwipRegisterAmount(false);
          unsubscribe();
        } else if (payload.status === 'error') {
          alert(`Failed to calculate: ${payload.error || 'Unknown error'}`);
          setIsLoadingCwipRegisterAmount(false);
          unsubscribe();
        }
      });

      const result = await window.payroll.run("calculate_column_sum", {
        inputFiles: [],
        options: {
          file_name: cwipFile,
          column_name: amountColumn
        }
      });

      if (!result.ok || !result.runId) {
        alert(`Failed to start calculation: ${result.error || 'Unknown error'}`);
        setIsLoadingCwipRegisterAmount(false);
        unsubscribe();
        return;
      }

      if (result.runId && !expectedRunId) {
        expectedRunId = String(result.runId);
      }

      setTimeout(() => {
        if (sumValue === null) {
          setIsLoadingCwipRegisterAmount(false);
          unsubscribe();
        }
      }, 60000);
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsLoadingCwipRegisterAmount(false);
    }
  };

  // FS Subline ID for Sublead amount: AS → 20015, Ind AS → 20001 (from getFrf)
  const getFsSubLineId = async (): Promise<number> => {
    try {
      const frfResult = await (window as any).sharePointAPI?.getFrf?.();
      const frf = frfResult?.success ? frfResult.frf : null;
      if (frf === "Accounting Standards (AS)") return 20015;
      if (frf === "Indian Accounting Standards (Ind AS)") return 20001;
    } catch (error) {
      console.warn("Failed to determine FRF for FS subline, falling back to 20015", error);
    }
    return 20015;
  };

  // Load Sublead amount (FS Subline 20015 for AS, 20001 for Ind AS)
  const loadSubleadAmount = async () => {
    setIsLoadingSubleadAmount(true);
    try {
      if (!window.payroll?.run) {
        alert('Payroll API not available');
        setIsLoadingSubleadAmount(false);
        return;
      }

      const fsSubLineId = await getFsSubLineId();

      let allStdout = "";
      let expectedRunId: string | null = null;
      let sumValue: number | null = null;

      const unsubscribe = window.payroll.onProgress((payload: any) => {
        const payloadRunId = String(payload.runId || '');
        if (!expectedRunId && payloadRunId) {
          expectedRunId = payloadRunId;
        }
        if (expectedRunId && payloadRunId !== expectedRunId) {
          return;
        }

        if (payload.stdout) {
          allStdout = String(payload.stdout);
          const extracted = extractJSONFromStdout(allStdout);
          if (extracted && extracted.sum !== undefined) {
            sumValue = Number(extracted.sum);
            setSubleadAmount(sumValue);
            setIsLoadingSubleadAmount(false);
            unsubscribe();
            return;
          }
        }

        if (payload.status === 'success') {
          if (allStdout && !sumValue) {
            const extracted = extractJSONFromStdout(allStdout);
            if (extracted && extracted.sum !== undefined) {
              sumValue = Number(extracted.sum);
              setSubleadAmount(sumValue);
            } else {
              // If no result found, set to 0
              setSubleadAmount(0);
            }
          } else if (!allStdout) {
            // If no stdout at all, set to 0
            setSubleadAmount(0);
          }
          setIsLoadingSubleadAmount(false);
          unsubscribe();
        } else if (payload.status === 'error') {
          // On error, set to 0 instead of showing alert
          setSubleadAmount(0);
          setIsLoadingSubleadAmount(false);
          unsubscribe();
        }
      });

      const result = await window.payroll.run("get_fs_subline_sum", {
        inputFiles: [],
        options: { fs_sub_line_id: fsSubLineId }
      });

      if (!result.ok || !result.runId) {
        // If failed to start, set to 0
        setSubleadAmount(0);
        setIsLoadingSubleadAmount(false);
        unsubscribe();
        return;
      }

      if (result.runId && !expectedRunId) {
        expectedRunId = String(result.runId);
      }

      setTimeout(() => {
        if (sumValue === null) {
          // If timeout and no value, set to 0
          setSubleadAmount(0);
          setIsLoadingSubleadAmount(false);
          unsubscribe();
        }
      }, 60000);
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsLoadingSubleadAmount(false);
    }
  };

  // Load reconciliation data
  const loadReconciliationData = async () => {
    setIsLoadingReconciliation(true);
    try {
      if (window.sharePointAPI?.downloadClientJson) {
        const result = await window.sharePointAPI.downloadClientJson('Execution_PPE_CWIPCompleteness.json');
        if (result && typeof result === 'object') {
          const data = (result as any).data || result;
          if (data && typeof data === 'object') {
            setUserReconciliations(data.userReconciliations || []);
            if (data.cwipRegisterAmount !== undefined && data.cwipRegisterAmount !== null) {
              setCwipRegisterAmount(Number(data.cwipRegisterAmount));
            }
            if (data.subleadAmount !== undefined && data.subleadAmount !== null) {
              setSubleadAmount(Number(data.subleadAmount));
            }
          }
        }
      }
    } catch (error) {
      console.log('Reconciliation file not found, will be created on save');
    } finally {
      setIsLoadingReconciliation(false);
    }
  };

  // Save reconciliation data
  const saveReconciliationData = async () => {
    setIsSavingReconciliation(true);
    try {
      const data = {
        userReconciliations: userReconciliations,
        cwipRegisterAmount: cwipRegisterAmount,
        subleadAmount: subleadAmount
      };
      const jsonString = JSON.stringify(data, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_PPE_CWIPCompleteness.json',
          referenceValue: 'CWIP Completeness Check',
          folderName: 'juggernaut',
          fyYear: fyYear,
          sectionList: ['Execution_PPE_CWIPCompleteness']
        });
        if (result.success) {
          alert('Reconciliation data saved successfully!');
        } else {
          alert(`Failed to save: ${result.error || 'Unknown error occurred'}`);
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Error saving reconciliation data:', error);
      alert(`Error saving reconciliation data: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    } finally {
      setIsSavingReconciliation(false);
    }
  };

  // Calculate difference
  const getDifference = (): number => {
    return cwipRegisterAmount - subleadAmount;
  };

  // Calculate net difference
  const getNetDifference = (): number => {
    const difference = getDifference();
    const reconciliationTotal = userReconciliations.reduce((sum, rec) => sum + (rec.amount || 0), 0);
    return difference - reconciliationTotal;
  };

  // Add user reconciliation row
  const addUserReconciliation = () => {
    setUserReconciliations([...userReconciliations, { reason: '', amount: 0 }]);
  };

  // Remove user reconciliation row
  const removeUserReconciliation = (index: number) => {
    setUserReconciliations(userReconciliations.filter((_, i) => i !== index));
  };

  // Update user reconciliation
  const updateUserReconciliation = (index: number, field: 'reason' | 'amount', value: string | number) => {
    setUserReconciliations(userReconciliations.map((rec, i) =>
      i === index ? { ...rec, [field]: value } : rec
    ));
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "PPE";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "CWIP-ANALYSIS-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  const canRun = () => !!(cwipFile && cutoffDate && amountColumn && dateColumn);

  const runCwip = async () => {
    if (!canRun()) return;

    // Save column mappings to file usage reference
    const entries: FileUsageEntry[] = [];
    const fileName = cwipFile.includes(' (') ? cwipFile.split(' (')[0] : cwipFile;
    entries.push({ "Search for files": fileName });
    const columnMapping: Record<string, string> = {
      amountColumn,
      dateColumn,
      cutoffDate
    };
    entries.push({ "Column Mapping": JSON.stringify(columnMapping) });
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Property, Plant & Equipment", "CWIP Analysis", entries);
      await loadFileUsageReference();
    }

    setProcessingStatus("running");
    setFileUrl("");

    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_ppe_cwip_analysis", {
          inputFiles: [],
          options: {
            excel_file: cwipFile,
            cutoff_date: cutoffDate,
            columns: [amountColumn.trim(), dateColumn.trim()]
          },
        });
        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          setProcessingStatus("error");
        }
      } else {
        setProcessingStatus("error");
      }
    } catch (e) {
      console.error("Error running CWIP Analysis:", e);
      setProcessingStatus("error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">CWIP Analysis - Property, Plant & Equipment</h2>
          <p className="text-gray-400">Upload CWIP listing, set cutoff, map columns, and run analysis</p>
        </div>
        <Button variant="outline" onClick={handleBack} className="border-white/10 text-white/80 hover:bg-white/10 hover:text-white">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Search className="h-5 w-5 text-blue-500" />
              Step 1: Search for Existing Files
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "found" && (
              <span className="text-green-400 font-bold"> FOUND!</span>
            )}
            {fileSearchStatus === "not_found" && (
              <span className="text-yellow-400">No files found</span>
            )}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-white">Select CWIP File</Label>
                  <Select value={cwipFile} onValueChange={setCwipFile}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select CWIP file" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {foundFiles.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fileUsageReference.find(e => e["Search for files"]) && (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {fileUsageReference.find(e => e["Search for files"])?.["Search for files"]}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Cutoff date</Label>
                  <Input
                    type="date"
                    value={cutoffDateISO}
                    onChange={(e) => handleCutoffDateChange(e.target.value)}
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
              </div>
              {cwipFile && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadExcelColumns} disabled={!cwipFile || isLoadingExcelColumns} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isLoadingExcelColumns ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>
                    ) : (
                      "Load Excel Columns"
                    )}
                  </Button>
                  {excelColumns.length > 0 && (
                    <span className="text-green-400 text-sm"> {excelColumns.length} columns loaded</span>
                  )}
                </div>
              )}
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">No files found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Cutoff date</Label>
                  <Input
                    type="date"
                    value={cutoffDateISO}
                    onChange={(e) => handleCutoffDateChange(e.target.value)}
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {uploadStatus === "completed" && (
                  <span className="text-green-400">File uploaded successfully!</span>
                )}
                {uploadStatus === "error" && (
                  <span className="text-red-400">Upload failed</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 2: Map Columns</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Map Columns"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Amount</Label>
              <Select value={amountColumn} onValueChange={setAmountColumn} disabled={excelColumns.length === 0}>
                <SelectTrigger><SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select column"} /></SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {excelColumns.map((col) => (<SelectItem key={col.value} value={col.value}>{col.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                try {
                  const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                  const lastUsed = mapping.amountColumn;
                  return lastUsed ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {lastUsed}
                    </p>
                  ) : null;
                } catch {
                  return null;
                }
              })()}
            </div>
            <div className="space-y-2">
              <Label className="text-white">GRN Date Column</Label>
              <Select value={dateColumn} onValueChange={setDateColumn} disabled={excelColumns.length === 0}>
                <SelectTrigger><SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select column"} /></SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {excelColumns.map((col) => (<SelectItem key={col.value} value={col.value}>{col.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {fileUsageReference.find(e => e["Column Mapping"]) && (() => {
                try {
                  const mapping = JSON.parse(fileUsageReference.find(e => e["Column Mapping"])?.["Column Mapping"] || "{}");
                  const lastUsed = mapping.dateColumn;
                  return lastUsed ? (
                    <p className="text-xs text-white/60 mt-1">
                      Last used: {lastUsed}
                    </p>
                  ) : null;
                } catch {
                  return null;
                }
              })()}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">GRN Date</Label>
              <Input
                type="date"
                value={grnDateISO}
                onChange={(e) => handleGrnDateChange(e.target.value)}
                className="border-white/10 bg-black/40 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5 text-green-500" />
              Execute CWIP Analysis
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute CWIP Analysis"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={runCwip} disabled={!canRun() || processingStatus === "running"} className="bg-green-600 hover:bg-green-700 text-white">
              {processingStatus === "running" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>) : ("Run CWIP Analysis")}
            </Button>
            {processingStatus === "completed" && (<span className="text-green-400">Successful</span>)}
            {processingStatus === "error" && (<span className="text-red-400">Failed</span>)}
          </div>
          {fileUrl && (
            <div className="mt-4 p-3 bg-green-900/20 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-sm">CWIP analysis completed! Results uploaded to SharePoint.</p>
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline text-sm">View Results</a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completeness Check Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Table className="h-5 w-5" />
              Completeness Check
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Completeness Check"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-white/10">
              <thead>
                <tr className="bg-black/60">
                  <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                  <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">As per CWIP Register (A)</td>
                  <td className="border border-white/10 px-4 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <Input
                        type="number"
                        value={cwipRegisterAmount !== undefined && cwipRegisterAmount !== null ? cwipRegisterAmount : ''}
                        onChange={(e) => setCwipRegisterAmount(parseFloat(e.target.value) || 0)}
                        className="border-white/10 bg-black/40 text-white text-right w-40"
                        placeholder="Enter amount"
                      />
                      {amountColumn && cwipFile && (
                        <Button
                          onClick={loadCwipRegisterAmount}
                          disabled={isLoadingCwipRegisterAmount}
                          size="sm"
                          variant="outline"
                          className="border-white/10 text-white"
                        >
                          {isLoadingCwipRegisterAmount ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</>
                          ) : (
                            'Auto Calculate'
                          )}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">As per Sublead (B)</td>
                  <td className="border border-white/10 px-4 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <Input
                        type="number"
                        value={subleadAmount !== undefined && subleadAmount !== null ? subleadAmount : ''}
                        onChange={(e) => setSubleadAmount(parseFloat(e.target.value) || 0)}
                        className="border-white/10 bg-black/40 text-white text-right w-40"
                        placeholder="Enter amount"
                      />
                      <Button
                        onClick={loadSubleadAmount}
                        disabled={isLoadingSubleadAmount}
                        size="sm"
                        variant="outline"
                        className="border-white/10 text-white"
                      >
                        {isLoadingSubleadAmount ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</>
                        ) : (
                          'Auto Calculate'
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Difference (C)</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {formatNumber(getDifference())}
                  </td>
                </tr>
                {userReconciliations.map((rec, index) => (
                  <tr key={`reconciliation-${index}`} className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">
                      <Input
                        type="text"
                        value={rec.reason}
                        onChange={(e) => updateUserReconciliation(index, 'reason', e.target.value)}
                        className="border-white/10 bg-black/40 text-white"
                        placeholder="Enter reason"
                      />
                    </td>
                    <td className="border border-white/10 px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={rec.amount || ''}
                          onChange={(e) => updateUserReconciliation(index, 'amount', parseFloat(e.target.value) || 0)}
                          className="border-white/10 bg-black/40 text-white text-right flex-1"
                          placeholder="Enter amount"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUserReconciliation(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {formatNumber(getNetDifference())}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center">
            <Button
              onClick={addUserReconciliation}
              size="sm"
              variant="outline"
              className="border-white/10 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
            <Button
              onClick={saveReconciliationData}
              disabled={isSavingReconciliation}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSavingReconciliation ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FileCheck2 className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Composition Analysis</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Composition Analysis"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">CWIP Composition Analysis</h3>
              <p className="text-sm text-gray-400 mb-4">Select columns from the CWIP file to analyze composition by unique values and amounts.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Select Columns for Analysis</Label>
              <div className="relative" ref={cwipCompositionDropdownRef}>
                <Button
                  variant="outline"
                  className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-white/10"
                  onClick={() => {
                    setShowCwipCompositionDropdown(!showCwipCompositionDropdown);
                    if (showCwipCompositionDropdown) setCwipCompositionColumnsSearch("");
                  }}
                  disabled={excelColumns.length === 0}
                >
                  {cwipCompositionColumns.size > 0
                    ? `${cwipCompositionColumns.size} column(s) selected`
                    : excelColumns.length === 0
                      ? "Load columns first"
                      : "Select columns"}
                </Button>
                {showCwipCompositionDropdown && excelColumns.length > 0 && (
                  <div className="absolute z-10 mt-2 w-full rounded-md border border-white/10 bg-black/90 p-3 max-h-64 overflow-auto">
                    <div className="mb-2 sticky top-0 bg-black/90 z-10">
                      <Input
                        type="text"
                        placeholder="Search columns..."
                        value={cwipCompositionColumnsSearch}
                        onChange={(e) => setCwipCompositionColumnsSearch(e.target.value)}
                        className="w-full border-white/10 bg-black/40 text-white placeholder:text-white/40"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="space-y-2">
                      {excelColumns
                        .filter((col) => col.name.toLowerCase().includes(cwipCompositionColumnsSearch.toLowerCase()) || col.value.toLowerCase().includes(cwipCompositionColumnsSearch.toLowerCase()))
                        .map((col) => (
                          <div key={col.value} className="flex items-center space-x-2 px-2 py-1 hover:bg-white/5 rounded">
                            <Checkbox
                              id={`cwip-comp-${col.value}`}
                              checked={cwipCompositionColumns.has(col.value)}
                              onCheckedChange={() => handleCwipCompositionColumnToggle(col.value)}
                            />
                            <Label htmlFor={`cwip-comp-${col.value}`} className="text-white text-sm cursor-pointer">
                              {col.name}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              {cwipCompositionColumns.size > 0 && (
                <div className="text-xs text-blue-300">
                  Selected: {Array.from(cwipCompositionColumns).join(', ')}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-white">Amount Column</Label>
              <Select value={cwipCompositionAmountCol} onValueChange={setCwipCompositionAmountCol} disabled={excelColumns.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select amount column"} />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {excelColumns.map((col) => (
                    <SelectItem key={col.value} value={col.value}>
                      {col.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={runCwipCompositionAnalysis}
                disabled={cwipCompositionColumns.size === 0 || !cwipCompositionAmountCol || isRunningCwipCompositionAnalysis || !cwipFile}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isRunningCwipCompositionAnalysis ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running Analysis...</>
                ) : (
                  "Run composition analysis for additions"
                )}
              </Button>
              {isRunningCwipCompositionAnalysis && (
                <span className="text-blue-400 text-sm">Processing...</span>
              )}
            </div>

            {/* Load Charts Button */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white/60">
                  Load composition analysis charts from SharePoint
                </div>
                <Button
                  onClick={loadCwipCompositionCharts}
                  disabled={isLoadingCwipCompositionData}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {isLoadingCwipCompositionData ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
                  ) : (
                    <><PieChart className="h-4 w-4 mr-2" /> Load Charts</>
                  )}
                </Button>
              </div>
            </div>

            {/* Display Loaded Charts */}
            {Object.keys(loadedCwipCompositionData).length > 0 && (
              <div className="space-y-8 mt-6 pt-6 border-t border-white/10">
                <h3 className="text-xl font-semibold text-white">CWIP Composition Analysis Results</h3>
                {Object.entries(loadedCwipCompositionData).map(([columnName, columnData]: [string, any]) => {
                  // Handle array format from JSON
                  let dataArray: any[] = [];
                  if (Array.isArray(columnData)) {
                    dataArray = columnData;
                  } else if (columnData && typeof columnData === 'object' && Array.isArray(columnData.data)) {
                    dataArray = columnData.data;
                  } else {
                    console.log(`Skipping ${columnName}: invalid format`, columnData);
                    return null;
                  }

                  const totalAmount = dataArray.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
                  const chartData = dataArray.map((item: any) => ({
                    name: String(item.label || ''),
                    value: Number(item.amount || 0)
                  })).filter(item => item.value > 0);

                  console.log(`Rendering chart for ${columnName}:`, { chartData, totalAmount });

                  return (
                    <div key={columnName} className="p-4 border border-white/20 rounded-lg bg-white/5">
                      <h4 className="text-lg font-semibold text-white mb-4">{columnName}</h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Pie Chart */}
                        {chartData && chartData.length > 0 ? (
                          <div className="w-full" style={{ minHeight: '320px', height: '320px' }}>
                            <ResponsiveContainer width="100%" height={320}>
                              <RechartsPieChart>
                                <Pie
                                  data={chartData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={false}
                                  outerRadius={100}
                                  fill="#8884d8"
                                  dataKey="value"
                                  nameKey="name"
                                >
                                  {chartData.map((entry: any, index: number) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip
                                  formatter={(value: any) => [
                                    `${formatNumber(Number(value))}`,
                                    'Amount'
                                  ]}
                                />
                                <Legend />
                              </RechartsPieChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div className="w-full h-80 flex items-center justify-center text-white/60 border border-white/10 rounded">
                            No chart data available
                          </div>
                        )}

                        {/* Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-white/20">
                            <thead>
                              <tr className="bg-black/60">
                                <th className="border border-white/20 px-4 py-2 text-left text-white font-semibold">Label</th>
                                <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Amount</th>
                                <th className="border border-white/20 px-4 py-2 text-right text-white font-semibold">Percentage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dataArray.map((item: any, idx: number) => {
                                const percentage = totalAmount > 0 ? ((item.amount || 0) / totalAmount * 100).toFixed(2) : '0.00';
                                return (
                                  <tr key={idx} className="hover:bg-white/5">
                                    <td className="border border-white/20 px-4 py-2 text-white">{String(item.label || '')}</td>
                                    <td className="border border-white/20 px-4 py-2 text-white text-right">
                                      {formatNumber(Number(item.amount || 0))}
                                    </td>
                                    <td className="border border-white/20 px-4 py-2 text-white text-right">{percentage}%</td>
                                  </tr>
                                );
                              })}
                              <tr className="bg-black/40 font-semibold">
                                <td className="border border-white/20 px-4 py-2 text-white">Total</td>
                                <td className="border border-white/20 px-4 py-2 text-white text-right">
                                  {formatNumber(totalAmount)}
                                </td>
                                <td className="border border-white/20 px-4 py-2 text-white text-right">100%</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {Object.keys(loadedCwipCompositionData).length === 0 && !isLoadingCwipCompositionData && (
              <div className="text-sm text-white/60 mt-4 pt-4 border-t border-white/10">
                No data loaded. Click "Load Charts" to download results from SharePoint.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="ppe"
            itemId="CWIP-ANALYSIS-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Step 2: Map Columns")]: "Step 2: Map Columns",
              [getSectionQuestionId("Execute CWIP Analysis")]: "Execute CWIP Analysis",
              [getSectionQuestionId("Completeness Check")]: "Completeness Check",
              [getSectionQuestionId("Composition Analysis")]: "Composition Analysis",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
